import { useMemo, useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { format, startOfMonth, startOfToday } from "date-fns";
import { th } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { OccurrenceRow } from "@/components/teacher/schedule/calendar/OccurrenceRow";
import type { WeeklyItem, ClassTemplateEvent } from "@/types/schedule";

interface TeacherScheduleCalendarProps { teacherCode?: string }

export function TeacherScheduleCalendar({ teacherCode }: TeacherScheduleCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(startOfToday());
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(startOfToday()));
  const [monthlyItems, setMonthlyItems] = useState<WeeklyItem[]>([]);
  const [events, setEvents] = useState<ClassTemplateEvent[]>([]);

  const monthStart = startOfMonth(viewMonth);
  const monthStartISO = format(monthStart, 'yyyy-MM-01');

  const fetchEvents = async () => {
    if (!teacherCode) return;
    const endDate = new Date(monthStart);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
    const { data } = await (supabase as any)
      .from('v_class_template_events')
      .select('*')
      .eq('requested_by', teacherCode)
      .gte('event_date', monthStartISO)
      .lte('event_date', format(endDate, 'yyyy-MM-dd'));
    setEvents((data ?? []) as ClassTemplateEvent[]);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teacherCode) { if (!cancelled) setMonthlyItems([]); return; }
      const { data: jobs, error } = await (supabase as any)
        .from('v_class_templates')
        .select('id, subject, location, weekday, start_time, end_time, students')
        .eq('teacher_code', teacherCode)
        .eq('is_active', true);

      if (cancelled || error) { setMonthlyItems([]); return; }
      const items: any[] = [];
      const year = monthStart.getFullYear();
      const month = monthStart.getMonth();
      for (let d = 1; d <= 31; d++) {
        const date = new Date(year, month, d);
        if (date.getMonth() !== month) break;
        const dow = date.getDay();
        const dateKey = format(date, 'yyyy-MM-dd');
        for (const j of jobs as any[]) {
          if (j.weekday === dow) {
            items.push({
              fixed_job_id: j.id,
              ui_key: `${j.id}-${dateKey}`,
              occurrence_id: null,
              date: dateKey,
              subject: j.subject,
              location: j.location,
              start_time: j.start_time,
              end_time: j.end_time,
              duration_minutes: 0,
              status: 'SCHEDULED',
              confirmed: false,
              edit_status: 'NONE',
              insufficient_hours: false,
              students: j.students,
              occurrence_edit_requests: [],
            });
          }
        }
      }
      setMonthlyItems(items);
    })();
    return () => { cancelled = true; };
  }, [teacherCode, monthStartISO]);

  useEffect(() => { fetchEvents(); }, [teacherCode, monthStartISO]);

  const monthlyByDate = useMemo(() => {
    const map: Record<string, WeeklyItem[]> = {};
    for (const item of monthlyItems) {
      const key = typeof item.date === 'string' ? item.date : format(new Date(item.date as any), 'yyyy-MM-dd');
      map[key] = map[key] || [];
      map[key].push(item);
    }
    return map;
  }, [monthlyItems]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="col-span-1">
        <CardContent className="pt-6">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            onMonthChange={(date) => date && setViewMonth(date)}
            locale={th}
            className="rounded-md border"
            modifiers={{
              hasCourse: (date) => {
                const dateKey = format(date, 'yyyy-MM-dd');
                return !!monthlyByDate[dateKey] && monthlyByDate[dateKey].length > 0;
              }
            }}
            modifiersClassNames={{
              selected: "bg-primary text-primary-foreground",
              hasCourse: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-primary"
            }}
          />
        </CardContent>
      </Card>

      <Card className="col-span-1 md:col-span-2">
        <CardContent className="pt-6 space-y-3">
          <div className="font-semibold">ตารางสอนของวันที่ {format(selectedDate,'yyyy-MM-dd')}</div>
          {(() => {
            const dateKey = format(selectedDate, 'yyyy-MM-dd');
            const items = monthlyByDate[dateKey] || [];
            if (!teacherCode) {
              return <div className="text-sm text-muted-foreground">กรุณากรอกรหัสครูในข้อมูลส่วนตัวเพื่อใช้งานส่วนนี้</div>;
            }
            if (monthlyItems.length === 0) {
              return <div className="text-sm text-muted-foreground">กำลังโหลด...</div>;
            }
            if (items.length === 0) {
              return <div className="text-sm text-muted-foreground">ไม่มีคาบตามตารางสัปดาห์</div>;
            }
            return (
              <div className="space-y-2">
                {items.sort((a,b)=>a.start_time.localeCompare(b.start_time)).map(item => (
                  <OccurrenceRow
                    key={(item as any).ui_key ?? `${item.fixed_job_id}-${item.date}`}
                    item={item}
                    teacherCode={teacherCode}
                    events={events}
                    onRefresh={fetchEvents}
                  />
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
